import { AnyAction, AsyncThunk } from '@reduxjs/toolkit';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { IntlContext } from '@/i18n';
import { AppDispatch } from '@/root-store';
import { ThunkResult } from '@/shared/actions/thunk-result';
import { CountryCode } from '@/shared/models/country-code';
import { IDocDefinition } from '@/shared/models/doc-definition';
import { UploadedFile } from '@/shared/models/uploaded-file';
import {
  ISubmitFormsActionParams,
  ISubmitFormsResult,
} from '@/shared/store/doc-uploader/async-actions';
import { IDocUploaderForm } from '@/shared/store/doc-uploader/types';
import {
  applyUploadFormChange,
  hasUnsentFile,
  needsNewFile,
} from '@/shared/store/doc-uploader/upload-form';
import { AtLeastOne } from '@/shared/types';

import { TOnChangeValue } from './DocUploadBlock';
import { UploadRelatedInfo } from './RelatedInfo';
import { IRelatedInfoHandle, RELATED_INFO_BY_DOC_TYPE, TUploadScope } from './RelatedInfo/config';
import { getSupplementaryGroups } from './SupplementaryDocs/config';
import {
  createSupplementaryDocs,
  getSupplementaryFormsToSubmit,
  isAllowedFile,
  isAnswered as isSupplementaryDocAnswered,
  ISupplementaryDoc,
  isSupplementaryDocValid,
  storeSupplementaryAnswer,
  TQuestionAnswer,
} from './SupplementaryDocs/supplementary-docs';
import {
  SupplementaryQuestions,
  TOnChangeSupplementaryValue,
} from './SupplementaryDocs/SupplementaryQuestions';
import { IUploadError } from './SupplementaryDocs/types';
import { UploadView } from './view';

interface IConnectedUploadProps<TDocType extends string> {
  onClickSkip: () => unknown;
  onFinishUpload: () => Promise<unknown> | unknown;
  onUploaded?: () => Promise<unknown> | unknown;
  onUpdateForm: (update: AtLeastOne<IDocUploaderForm<TDocType>>) => AnyAction;
  onSubmitForms: AsyncThunk<
    ThunkResult<ISubmitFormsResult<TDocType>>,
    ISubmitFormsActionParams<TDocType>,
    any
  >;
  selectUploadForm: (state: any) => IDocUploaderForm<TDocType>;
  selectUploadDefinition: (state: any) => IDocDefinition<TDocType> | undefined;
  selectDocDefinitions: (state: any) => IDocDefinition<TDocType>[];
  selectUploadedDocs: (state: any) => Partial<Record<TDocType, UploadedFile<TDocType>>>;
  selectHolderId: (state: any) => string | undefined;
  scope?: TUploadScope;
}

const ConnectedUpload = <TDocType extends string>({
  onUpdateForm,
  onSubmitForms,
  onClickSkip,
  onFinishUpload,
  onUploaded,
  selectUploadForm,
  selectUploadDefinition,
  selectDocDefinitions,
  selectUploadedDocs,
  selectHolderId,
  scope,
}: IConnectedUploadProps<TDocType>) => {
  const intl = useIntl();
  const dispatch: AppDispatch = useDispatch();
  const externalLinks = useExternalLinks();
  const { region } = useContext(IntlContext);
  const uploadDefinition = useSelector(selectUploadDefinition);
  const form = useSelector(selectUploadForm);
  const docDefinitions = useSelector(selectDocDefinitions);
  const uploadedDocs = useSelector(selectUploadedDocs);
  const holderId = useSelector(selectHolderId);

  const isGreatBritain = region === CountryCode.GreatBritain;
  const relatedInfoKind =
    form.file && scope && isGreatBritain
      ? RELATED_INFO_BY_DOC_TYPE[scope][form.docType]
      : undefined;
  const supplementaryGroup = scope
    ? getSupplementaryGroups(scope, region)[form.docType]
    : undefined;

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<IUploadError>();
  const [supplementaryDocs, setSupplementaryDocs] = useState(() =>
    createSupplementaryDocs(supplementaryGroup, docDefinitions, uploadedDocs, holderId),
  );
  const relatedInfoRef = useRef<IRelatedInfoHandle>(null);

  // a group whose documents the backend does not define has nothing to ask about
  const activeGroup = supplementaryDocs.length > 0 ? supplementaryGroup : undefined;

  const canSubmitWithoutFile =
    Boolean(form.file) && Boolean(relatedInfoKind || activeGroup) && !needsNewFile(form);
  const isAnswered = supplementaryDocs.every(isSupplementaryDocAnswered);
  const isReady = supplementaryDocs.every(doc => isSupplementaryDocValid(intl, doc));

  const clearUploadErrorFor = (docType: string) =>
    setUploadError(prev => (prev?.docType === docType ? undefined : prev));

  const updateSupplementaryDoc = (
    docType: TDocType,
    update: (doc: ISupplementaryDoc<TDocType>) => ISupplementaryDoc<TDocType>,
  ) =>
    setSupplementaryDocs(docs =>
      docs.map(doc => (doc.form.docType === docType ? update(doc) : doc)),
    );

  const handleChangeValue = useCallback<TOnChangeValue<TDocType>>(
    (_, { value, name }) => {
      const fileFormat = supplementaryGroup?.fileFormat;
      const file =
        name === 'file' ? (value as IDocUploaderForm<TDocType>['file'])?.file : undefined;

      if (fileFormat && !isAllowedFile(fileFormat, file)) {
        setUploadError({
          docType: form.docType,
          message: intl.formatMessage(fileFormat.wrongFormatError),
        });

        return;
      }

      dispatch(onUpdateForm(applyUploadFormChange(form, name, value)));

      if ('file' === name) {
        clearUploadErrorFor(form.docType);
      }
    },
    [dispatch, onUpdateForm, form, supplementaryGroup, intl],
  );

  const handleChangeSupplementaryValue: TOnChangeSupplementaryValue<TDocType> = (
    docType,
    { name, value },
  ) => {
    const question = supplementaryDocs.find(doc => doc.form.docType === docType)?.question;
    const file = name === 'file' ? (value as IDocUploaderForm<TDocType>['file'])?.file : undefined;

    if (question?.fileFormat && !isAllowedFile(question.fileFormat, file)) {
      setUploadError({
        docType,
        message: intl.formatMessage(question.fileFormat.wrongFormatError),
      });

      return;
    }

    updateSupplementaryDoc(docType, doc => ({
      ...doc,
      form: applyUploadFormChange(doc.form, name, value),
    }));

    if ('file' === name) {
      clearUploadErrorFor(docType);
    }
  };

  const handleAnswer = (docType: TDocType, answer: TQuestionAnswer) => {
    const doc = supplementaryDocs.find(({ form: { docType: type } }) => type === docType);

    if (!doc) {
      return;
    }

    storeSupplementaryAnswer(doc, answer);
    updateSupplementaryDoc(docType, current => ({ ...current, answer }));
  };

  const handleSubmit = async () => {
    setIsUploading(true);

    try {
      if (relatedInfoRef.current) {
        const isRelatedInfoSaved = await relatedInfoRef.current.submit();

        if (!isRelatedInfoSaved) {
          return;
        }
      }

      const forms = [
        ...(hasUnsentFile(form) ? [form] : []),
        ...getSupplementaryFormsToSubmit(supplementaryDocs),
      ];

      if (forms.length > 0) {
        const resultAction = await dispatch(
          onSubmitForms({
            forms,
            textDefaultError: intl.formatMessage({
              defaultMessage: 'File upload failed',
              id: 'nLx3K4',
              description: 'default file upload error',
            }),
          }),
        );

        if (!onSubmitForms.fulfilled.match(resultAction)) {
          return;
        }

        const { isSucceed, result } = resultAction.payload;
        const uploadedDocTypes = result?.uploadedDocTypes ?? [];

        if (uploadedDocTypes.includes(form.docType)) {
          dispatch(onUpdateForm({ status: 'pending' }));
        }

        setSupplementaryDocs(docs =>
          docs.map(doc =>
            uploadedDocTypes.includes(doc.form.docType)
              ? { ...doc, form: { ...doc.form, status: 'pending' } }
              : doc,
          ),
        );

        if (uploadedDocTypes.length > 0) {
          await onUploaded?.();
        }

        if (!isSucceed) {
          if (result?.failedDocType) {
            setUploadError({ docType: result.failedDocType, message: result.errorMessage ?? '' });
          }

          return;
        }
      }

      await onFinishUpload();
    } finally {
      setIsUploading(false);
    }
  };

  const saveHint = (() => {
    if (!activeGroup?.hints) {
      return undefined;
    }

    if (!isAnswered) {
      return intl.formatMessage(activeGroup.hints.answerQuestions);
    }

    return isReady ? undefined : intl.formatMessage(activeGroup.hints.uploadRemaining);
  })();

  return (
    <UploadView
      onClickSkip={onClickSkip}
      onSubmit={handleSubmit}
      docDefinition={uploadDefinition}
      form={form}
      isUploading={isUploading}
      onChangeValue={handleChangeValue}
      accept={supplementaryGroup?.fileFormat?.extensions.join(',')}
      fileTypes={supplementaryGroup?.fileFormat?.extensions}
      canSubmitWithoutFile={canSubmitWithoutFile}
      canSubmit={isAnswered && isReady}
      saveHint={saveHint}
      uploadError={uploadError?.docType === form.docType ? uploadError.message : undefined}
      groupTitle={activeGroup?.title && intl.formatMessage(activeGroup.title)}
      relatedInfoSlot={
        relatedInfoKind &&
        scope && <UploadRelatedInfo ref={relatedInfoRef} kind={relatedInfoKind} scope={scope} />
      }
      supplementaryDocsSlot={
        activeGroup && (
          <SupplementaryQuestions
            group={activeGroup}
            docs={supplementaryDocs}
            isUploading={isUploading}
            uploadError={uploadError}
            learnMoreHref={activeGroup.learnMoreLink && externalLinks[activeGroup.learnMoreLink]}
            onChangeValue={handleChangeSupplementaryValue}
            onAnswer={handleAnswer}
          />
        )
      }
    />
  );
};

export type { IConnectedUploadProps };
export { ConnectedUpload };
